import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useContext, useMemo } from "react";
import { hasCapability } from "@vninvestcharts/engine-core";
import { UNKNOWN_AVAILABILITY, UNKNOWN_DEVICE, isUsable, resolveCapabilities, } from "../capabilities/CapabilityResolver";
import { useWidgetI18n } from "../context/WidgetI18nContext";
const CapabilityContext = createContext(null);
export function CapabilityProvider({ tier, tierSource = "none", backend = UNKNOWN_AVAILABILITY, device = UNKNOWN_DEVICE, forcedReason, narrowing, children, }) {
    const value = useMemo(() => ({
        tier: tier ?? null,
        backend,
        device,
        caps: resolveCapabilities({ tier, backend, device, forcedReason, narrowing }),
        tierSource,
    }), [tier, tierSource, backend, device, forcedReason, narrowing]);
    return _jsx(CapabilityContext.Provider, { value: value, children: children });
}
export function useResolvedCapabilities() {
    const ctx = useContext(CapabilityContext);
    if (ctx)
        return ctx;
    // Ngoài provider: coi như CHƯA xác minh ⇒ chỉ Community (fail-closed).
    const caps = resolveCapabilities({ tier: null });
    return { tier: null, backend: UNKNOWN_AVAILABILITY, device: UNKNOWN_DEVICE, caps, tierSource: "none" };
}
/** Hook gate rút gọn: `const can = useCapabilityGate("export_png")`. */
export function useCapabilityGate(capability) {
    const { caps } = useResolvedCapabilities();
    return caps[capability] ?? { state: "locked", requires: "enterprise" };
}
/** `true` khi capability dùng được (granted). */
export function useHasCapability(capability) {
    return isUsable(useCapabilityGate(capability));
}
/** Tương đương `hasCapability` nhưng đọc tier từ context. */
export function useTierAllows(capability) {
    const { tier } = useResolvedCapabilities();
    return hasCapability(tier, capability);
}
const REASON_KEY = {
    backend_missing: "capability.unavailable.backend",
    device_limited: "capability.unavailable.device",
    perf_budget: "capability.unavailable.perf",
    quota_local: "capability.unavailable.quota",
    data_unavailable: "capability.unavailable.data",
};
const TIER_LABEL_KEY = {
    none: "free",
    free: "free",
    starter: "starter",
    pro: "pro",
    enterprise: "enterprise",
    custom: "custom",
    internal: "internal",
};
/** Tên hiển thị của gói cần để mở capability (dùng trong upsell). */
function planLabelKey(tier) {
    return `plan.${TIER_LABEL_KEY[tier]}`;
}
/**
 * Giá của từng gói, theo key i18n (PRICING 2026-10-22).
 *
 * Nguồn sự thật: `PLAN_DEV_PHASE_OFFICIAL_2026-10-22.md` §1.1 (D1) — Pro 199.000đ/tháng.
 * Chỉ gói **có giá niêm yết** mới có mục ở đây:
 *   - `pro`        → 199.000đ/tháng (gói trả phí duy nhất hiện có)
 *   - `enterprise` → "Liên hệ" (12 capability Track B **chưa giao** ⇒ không bán được,
 *                    nên chỉ mời liên hệ chứ không niêm yết giá)
 *
 * `starter` không có mục: nó là bậc miễn phí (cổng `audit:paid-capability` xếp
 * `starter` vào `FREE_TIERS`) — giới hạn duy nhất của nó là multi-chart 2 cột mặc
 * định (`src/widget/multiChartDefaults.ts:17`), Pro là 4 cột.
 */
const PLAN_PRICE_KEY = {
    pro: "plan.pro.price",
    enterprise: "plan.enterprise.price",
};
export function CapabilityGate({ capability, children }) {
    const state = useCapabilityGate(capability);
    // Narrow trực tiếp theo discriminant (isUsable trả boolean, không phải type guard).
    if (state.state === "granted")
        return _jsx(_Fragment, { children: children });
    if (state.state === "degraded") {
        return _jsx(UnavailableState, { reason: state.reason, capability: capability });
    }
    return _jsx(LockedFeature, { capability: capability, requiredTier: state.requires });
}
export function LockedFeature({ capability, requiredTier, onLockedClick, }) {
    const { t } = useWidgetI18n();
    const title = tOrKey(t, `capability.upsell.${capability}.title`);
    const benefit = tOrKey(t, `capability.upsell.${capability}.benefit`);
    // Gói nào mở được + giá gói đó (PRICING 2026-10-22). Trước đây upsell chỉ
    // hiện tên tính năng ⇒ người dùng không biết cần mua gói nào, giá bao nhiêu.
    const plan = tOrKey(t, planLabelKey(requiredTier));
    const priceKey = PLAN_PRICE_KEY[requiredTier];
    const price = priceKey ? tOrKey(t, priceKey) : null;
    return (_jsxs("button", { type: "button", className: "vnsc-locked-feature", "aria-label": price ? `${t("capability.locked.icon")} — ${plan}, ${price}` : t("capability.locked.icon"), title: `${title} — ${benefit}`, onClick: onLockedClick, "data-capability": capability, "data-required-tier": requiredTier, children: [_jsx("span", { className: "vnsc-locked-feature__icon", "aria-hidden": "true", children: "\uD83D\uDD12" }), _jsx("span", { className: "vnsc-locked-feature__label", children: title }), _jsx("span", { className: "vnsc-locked-feature__plan", children: plan }), price ? _jsx("span", { className: "vnsc-locked-feature__price", children: price }) : null] }));
}
export function UnavailableState({ reason, capability, compact = false, }) {
    const { t } = useWidgetI18n();
    const message = tOrKey(t, REASON_KEY[reason]);
    return (_jsxs("div", { className: compact ? "vnsc-unavailable vnsc-unavailable--compact" : "vnsc-unavailable", role: "status", "data-reason": reason, "data-capability": capability ?? "", children: [_jsx("strong", { className: "vnsc-unavailable__title", children: t("capability.unavailable.title") }), _jsx("span", { className: "vnsc-unavailable__body", children: message })] }));
}
/** Chỉ dùng trong test/headless: tương đương `t()` nhưng có fallback rõ ràng. */
function tOrKey(t, key) {
    const res = t(key);
    return res === key ? key : res;
}
export { REASON_KEY, planLabelKey };
